import { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { appsApi, type App, type ApiKey, type IssuedKey } from "../api/apps";

export function AppDetailPage() {
  const { appId } = useParams<{ appId: string }>();
  const [app, setApp] = useState<App | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [issuing, setIssuing] = useState(false);
  const [label, setLabel] = useState("");
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  async function refresh() {
    if (!appId) return;
    const res = await appsApi.get(appId);
    setApp(res.app);
    setApiKeys(res.apiKeys);
  }

  useEffect(() => {
    refresh();
  }, [appId]);

  async function issue() {
    if (!appId) return;
    const res = await appsApi.issueKey(appId, label || undefined);
    setIssued(res);
    setIssuing(false);
    setLabel("");
    await refresh();
  }

  async function revoke(keyId: string) {
    if (!appId) return;
    if (!confirm("Revoke this key? Existing senders will break immediately.")) return;
    await appsApi.revokeKey(appId, keyId);
    await refresh();
  }

  if (!app) return <Typography color="text.secondary">Loading…</Typography>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button component={RouterLink} to="/apps" size="small">
            ← All apps
          </Button>
          <Typography variant="h4" mt={1}>
            {app.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {app.appId} · {app.ownerEmail}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setIssuing(true)}>
          Issue API key
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            API keys
          </Typography>
          {apiKeys.length === 0 ? (
            <Typography color="text.secondary">No keys yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Last 4</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((k) => (
                  <TableRow key={k.keyId}>
                    <TableCell>{k.label ?? "—"}</TableCell>
                    <TableCell>
                      <code>…{k.lastFour}</code>
                    </TableCell>
                    <TableCell>{new Date(k.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {k.revokedAt ? (
                        <Chip label="Revoked" size="small" color="error" />
                      ) : (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {!k.revokedAt && (
                        <IconButton size="small" onClick={() => revoke(k.keyId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={issuing} onClose={() => setIssuing(false)} fullWidth maxWidth="sm">
        <DialogTitle>Issue new API key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            helperText="e.g. ‘production server’ or ‘CI tests’"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuing(false)}>Cancel</Button>
          <Button variant="contained" onClick={issue}>
            Issue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!issued} onClose={() => setIssued(null)} fullWidth maxWidth="sm">
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the full key will be shown. Copy it now and store it securely.
          </Alert>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              value={issued?.plaintext ?? ""}
              fullWidth
              InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
            />
            <IconButton
              onClick={() => {
                if (issued?.plaintext) navigator.clipboard.writeText(issued.plaintext);
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIssued(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
